import { useCurrentItem } from '@gleamer/gdata-extension';
import { Internal } from '@gleamer/types';
import { ServicesManager } from '@ohif/core';
import { format, isValid, parse } from 'date-fns';
import React, { Fragment, useEffect, useMemo } from 'react';
import {
  useDisplayedPredictionsBySeries,
  useSetStudiesOrder,
} from '../../contexts/predictions.hooks';
import { getDroppedStudies } from '../../preAnnotations/droppedPreAnnotations';

type Detection = Internal.DetectionInternal;
type Prediction = Internal.PredictionInternal;

type DetectionsListProps = {
  servicesManager: ServicesManager;
  renderDetection: ({
    detection,
    prediction,
    study,
    series,
    index,
  }: {
    detection: Detection;
    prediction: Prediction;
    study: Internal.DicomJSONStudy;
    series: Internal.DicomJSONSeries;
    index: number;
  }) => React.ReactNode;
};

/** A dropped DICOM may have no StudyDate, or a malformed one: format throws on those. */
function formatStudyDate(studyDate: string | undefined): string {
  const date = parse(studyDate ?? '', 'yyyyMMdd', new Date());
  return isValid(date) ? format(date, 'dd-MMM-yyyy') : 'Unknown date';
}

export function DetectionsList({
  servicesManager,
  renderDetection,
}: DetectionsListProps) {
  const currentItem = useCurrentItem({ servicesManager });
  // Outside a task (Pre-annotations dropped in /local-bac) there is no current
  // item: list the dropped studies.
  const studies = useMemo(
    () => currentItem?.studies ?? getDroppedStudies(),
    [currentItem]
  );
  const setStudiesOrder = useSetStudiesOrder();
  const predictionsByStudies = useDisplayedPredictionsBySeries();

  useEffect(() => {
    setStudiesOrder(
      studies.map((study) => ({
        studyInstanceUID: study.StudyInstanceUID,
        seriesInstanceUIDs: study.series.map(
          (series) => series.SeriesInstanceUID
        ),
      }))
    );
  }, [studies]);

  if (!predictionsByStudies) {
    return null;
  }

  const studiesWithPredictions = studies.filter(
    (study) => !!predictionsByStudies[study.StudyInstanceUID]
  );

  return (
    <ul className="ohif-scrollbar overflow-y-auto pb-3">
      {studiesWithPredictions
        .filter((study) => predictionsByStudies[study.StudyInstanceUID])
        .map((study) => (
          <Fragment key={study.StudyInstanceUID}>
            {/* the study, then the series, each sticky: the series under the study */}
            <li className="sticky top-0 z-[2] flex h-9 items-center border-b border-dl-strong bg-[#2A2730] px-3.5 text-[13px] font-bold uppercase tracking-[.06em] text-[#F4F2F6]">
              {formatStudyDate(study.StudyDate)}
            </li>
            {study.series
              .filter(
                (series) =>
                  predictionsByStudies[study.StudyInstanceUID][
                    series.SeriesInstanceUID
                  ]
              )
              .map((series) => (
                <Fragment key={series.SeriesInstanceUID}>
                  <li className="sticky top-9 z-[1] mb-1 flex h-8 items-center border-y border-b-dl-border border-t-dl-strong bg-[#201D25] px-3.5 text-xs font-bold uppercase tracking-widest text-[#E2DEE8]">
                    S: {series.SeriesNumber}
                  </li>
                  {predictionsByStudies[study.StudyInstanceUID][
                    series.SeriesInstanceUID
                  ].flatMap((prediction) =>
                    prediction.detections.map((detection, index) =>
                      renderDetection({
                        detection,
                        prediction,
                        study,
                        series,
                        index,
                      })
                    )
                  )}
                </Fragment>
              ))}
          </Fragment>
        ))}
    </ul>
  );
}
